'use client';

import { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { useCookies } from 'next-client-cookies';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, AtSignIcon, KeySquareIcon, LockIcon, LockKeyholeOpenIcon } from 'lucide-react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupPassword } from '@/components/ui/input-group';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { useCartStore, useLocalCartStore, useLoginStore, useLogoutStore, useMeStore } from '@/store';
import { getErrorMessage, getLocalizedRouteFromKeys, setValidationErrors } from '@/lib/utils';
import { getCookieOptions } from '@/lib/cookie-utils';
import { useAppLocale, useMetadata } from '@/hooks';
import { EMAIL_REGEX, PASSWORD_REGEX } from '@/constants';
import { LoginParamsType, TokensType } from '@/types/auth';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
type PropsType = {
  idPrefix?: string;
  redirect?: boolean;
  next?: string;
  noHeader?: boolean;
  onLoginSuccess?: () => void;
  onNavigate?: () => void;
  className?: string;
};
const LoginForm = ({
  idPrefix,
  redirect = false,
  next,
  noHeader = false,
  onLoginSuccess,
  onNavigate,
  className = ''
}: PropsType) => {
  const {
    backendLocale
  } = useAppLocale();
  const {
    routeParams
  } = useMetadata();
  const t = useTranslations();
  const cookies = useCookies();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectPath = searchParams.get('next') || next || null;
  const {
    loginState,
    login
  } = useLoginStore();
  const {
    meState,
    getMe,
    reset
  } = useMeStore();
  const {
    addItem,
    getCart
  } = useCartStore();
  const {
    clearItems
  } = useLocalCartStore();
  const [authError, setAuthError] = useState<string | null>(null);
  const [formDisabled, setFormDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const registerRoute = getLocalizedRouteFromKeys('/register', backendLocale, routeParams);
  const passwordRoute = getLocalizedRouteFromKeys('/password', backendLocale, routeParams);
  const formSchema = z.object({
    email: z.string().trim().transform(val => val.toLowerCase()).refine(val_0 => EMAIL_REGEX.test(val_0), t('invalid_email')),
    password: z.string().min(6, t('min_value', {
      value: 6
    })).max(32, t('max_value', {
      value: 32
    })).refine(val_1 => PASSWORD_REGEX.test(val_1), t('password_pattern')).refine((val_2: string) => !/\s/.test(val_2), t('password_pattern')),
    rememberMe: z.boolean().optional()
  });
  const {
    mutate
  } = useMutation({
    mutationFn: async (data: LoginParamsType) => {
      setLoading(true);
      useLogoutStore.getState().reset();
      return await login(data);
    },
    onSuccess: async (response: TokensType, _: LoginParamsType) => {
      setAuthError(null);
      const rememberMe = form.getValues('rememberMe');
      const cookieOptions = getCookieOptions(rememberMe);
      cookies.set('accessToken', response.accessToken, cookieOptions);
      cookies.set('refreshToken', response.refreshToken, cookieOptions);
      // Store rememberMe preference as a long-lived cookie so refresh flow can read it
      const rememberMeOptions = getCookieOptions(true); // always persistent
      cookies.set('rememberMe', rememberMe ? 'true' : 'false', rememberMeOptions);
      await getMe();
      const guestItems = useLocalCartStore.getState().items;
      if (guestItems.length > 0) {
        try {
          for (const guestItem of guestItems) {
            await addItem({
              productId: guestItem.productId,
              quantity: guestItem.quantity
            });
          }
          clearItems();
          await getCart();
        } catch (error: any) {
          toast.error(getErrorMessage(error, t('something_went_wrong')));
        }
      }
      toast.success(t('login_success'));
      if (onLoginSuccess) {
        onLoginSuccess();
      }
      if (!redirect) {
        return;
      }
      router.refresh();
      if (redirectPath) {
        router.push(redirectPath);
      } else {
        router.push('/');
      }
    },
    onError: (error_0: any) => {
      const message = getErrorMessage(error_0, t('something_went_wrong'));
      if (error_0.response?.status === 401) {
        setAuthError(message);
      } else if (error_0.response?.status === 422) {
        setValidationErrors(error_0, form.setError);
        setAuthError(null);
      } else {
        setAuthError(null);
        reset();
      }
    },
    onSettled: () => {
      setLoading(false);
    }
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    }
  });
  const onSubmit = (data_0: z.infer<typeof formSchema>) => {
    mutate({
      email: data_0.email,
      password: data_0.password
    });
  };
  useEffect(() => {
    if (meState.isLoading || meState.response !== null || loginState.isLoading) {
      setFormDisabled(true);
    } else {
      setFormDisabled(false);
    }
  }, [form.formState, meState.isLoading, meState.response, loginState.isLoading]);
  return <Form {...form}>
      <div className={`flex w-full flex-col space-y-2 select-none ${className}`}>
        {!noHeader && <h3 className="mb-4 flex flex-row items-center justify-items-center space-x-2 rounded-md border-b-2 bg-gray-100 px-3 py-2 text-sm font-medium uppercase">
            <LockIcon size="18" />
            <span>{t('login')}</span>
          </h3>}
        {authError && <Alert variant="destructive" className="mb-4" onClose={() => setAuthError(null)}>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('error')}</AlertTitle>
            <AlertDescription>{authError}</AlertDescription>
          </Alert>}
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField control={form.control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel required htmlFor={`${idPrefix ? idPrefix + '-' : ''}login-email`}>
                  {t('email')}
                </FormLabel>
                <FormControl>
                  <InputGroup>
                    <InputGroupInput type="email" id={`${idPrefix ? idPrefix + '-' : ''}login-email`} placeholder={t('enter_your_email')} {...field} autoFocus disabled={formDisabled} />
                    <InputGroupAddon>
                      <AtSignIcon />
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={form.control} name="password" render={({
          field: field_0
        }) => <FormItem>
                <FormLabel required htmlFor={`${idPrefix ? idPrefix + '-' : ''}login-password`}>
                  {t('password')}
                </FormLabel>
                <FormControl>
                  <InputGroup>
                    <InputGroupPassword id={`${idPrefix ? idPrefix + '-' : ''}login-password`} placeholder={t('enter_your_password')} disabled={formDisabled} {...field_0} />
                    <InputGroupAddon>
                      <KeySquareIcon />
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={form.control} name="rememberMe" render={({
          field: field_1
        }) => <FormItem className="flex flex-row items-center">
                <FormControl>
                  <Checkbox checked={field_1.value} onCheckedChange={field_1.onChange} disabled={formDisabled} />
                </FormControl>
                <FormLabel className="cursor-pointer font-normal">{t('remember_me')}</FormLabel>
              </FormItem>} />
          <Button type="submit" className="w-full uppercase" loading={loading} disabled={loading || formDisabled}>
            <span>{t('login')}</span>
            <LockKeyholeOpenIcon />
          </Button>
        </form>
        <Separator />
        <div className="flex flex-row items-center justify-between gap-2 text-sm">
          <Link href={registerRoute} onClick={onNavigate} className="text-gray-600 transition-all duration-300 hover:text-gray-900 hover:underline">
            {t('register')}
          </Link>
          <Link href={passwordRoute} onClick={onNavigate} className="text-gray-600 transition-all duration-300 hover:text-gray-900 hover:underline">
            {t('lost_password')}
          </Link>
        </div>
      </div>
    </Form>;
};
export default LoginForm;